<i18n locale="en">
{
  "add": "Add Card",
  "delete": "Delete card",
  "labels": {
    "cards": "Cards",
    "title": "Title"
  }
}
</i18n>

<template>
  <EditPanel
    :is-editing="
      selectedIndex !== null &&
      currentBlockParams.cards[selectedIndex] !== undefined
    "
  >
    <template #preview>
      <EditorLayout>
        <div class="cards-editor flex-col gap-8">
          <OwnInputContainer
            :label="t('labels.title')"
            :errors="title.status.errors"
            :max-chars="MAX_TITLE_LENGTH"
            :value="title.value"
          >
            <OwnInput v-model="title.value" />
          </OwnInputContainer>
          <div class="flex-col gap-3">
            <OwnInputContainer :label="t('labels.cards')">
              <Reorderable
                v-slot="{ item, index }"
                v-model="currentBlockParams.cards"
                item-key="title"
                row-class="mt-2"
                @reorder="onReorder"
              >
                <OwnOption @click="onEdit(index)">
                  <div class="flex-row gap-4">
                    <DragHandle />
                    <OwnType
                      class="flex-1"
                      color="primary"
                      variant="title-regular"
                      :text="item.title"
                    />
                    <OwnButton
                      :aria-label="t('delete')"
                      class="cards-editor__delete-button"
                      @click="onDelete(index)"
                    >
                      <template #icon-left>
                        <PhTrash size="24" />
                      </template>
                    </OwnButton>
                  </div>
                </OwnOption>
              </Reorderable>
            </OwnInputContainer>
            <OwnButton :text="t('add')" @click="onNew" />
          </div>
        </div>

        <template #actions>
          <slot />
        </template>
      </EditorLayout>
    </template>
    <template #edit>
      <CardDetailEditor
        class="cards-editor__child"
        :item="currentBlockParams.cards[selectedIndex]"
        @back="onBack"
        @update="onUpdate"
      />
    </template>
  </EditPanel>
</template>

<script>
import { PhTrash } from '@phosphor-icons/vue'
import { useI18n } from 'vue-i18n'
import { mapGetters } from 'vuex'

import { FormBuilder, Validators } from '@/forms'
import EditorLayout from '@/lib/builder/public/editor/EditorLayout.vue'
import EditPanel from '@/lib/builder/public/editor/EditPanel.vue'
import { OwnButton } from '@/ui/OwnButton'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnOption } from '@/ui/OwnOption'
import { OwnType } from '@/ui/OwnType'
import DragHandle from '@/ui/reorderable/DragHandle.vue'
import Reorderable from '@/ui/reorderable/Reorderable.vue'

import { CardsCommands } from '../commands/CardsCommands'

import CardDetailEditor from './CardDetailEditor.vue'

const MAX_TITLE_LENGTH = 50
export default {
  name: 'CardsEditor',
  components: {
    CardDetailEditor,
    DragHandle,
    EditPanel,
    EditorLayout,
    OwnButton,
    OwnInput,
    OwnInputContainer,
    OwnOption,
    OwnType,
    PhTrash,
    Reorderable,
  },
  mixins: [
    FormBuilder({
      title: {
        debounce: 250,
        validateOnInit: true,
        validators: [
          Validators.required,
          Validators.maxLength(MAX_TITLE_LENGTH),
        ],
      },
    }),
  ],
  props: {
    id: { type: String, required: true },
  },
  setup() {
    const { t } = useI18n()

    return { t }
  },
  data() {
    return {
      MAX_TITLE_LENGTH,
      selectedIndex: null,
    }
  },
  computed: {
    ...mapGetters({
      blockById: 'websiteBuilder/blockById',
    }),
    currentBlock() {
      const { id } = this

      return this.blockById(id)
    },
    currentBlockParams() {
      const { currentBlock } = this

      return currentBlock?.params
    },
  },
  created() {
    const { currentBlockParams } = this

    this.setInitialFormValues({
      title: currentBlockParams.title,
    })
  },
  methods: {
    async applyCardsUpdate(newValue) {
      const { currentBlockParams, id } = this

      await CardsCommands.updateBlock({
        targetId: id,
        update: {
          params: {
            ...currentBlockParams,
            ...newValue,
          },
        },
      })
    },
    onBack() {
      this.selectedIndex = null
    },
    async onDelete(index) {
      await CardsCommands.deleteCard(this.currentBlock, index)
    },
    onEdit(index) {
      this.selectedIndex = index
    },
    async onFormControlUpdate(controlName, value, status) {
      if (!status.valid) {
        return
      }

      await this.applyCardsUpdate({
        [controlName]: value,
      })
    },
    async onNew() {
      this.selectedIndex = await CardsCommands.createCard(this.currentBlock)
    },
    async onReorder({ newIndex, oldIndex }) {
      await CardsCommands.reorderCard(this.currentBlock, oldIndex, newIndex)
    },
    async onUpdate(updatedCard) {
      await CardsCommands.updateCard(
        this.currentBlock,
        this.selectedIndex,
        updatedCard
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/styles/globals';

.cards-editor {
  &__delete-button {
    padding: 0;
    box-shadow: none;
  }
  &__child {
    width: calc(100% - 8px);

    @include globals.flyout-shadow();
  }
}
</style>
